/**
 * Widget mode store extension. Reads `widgetMode`, `wm`, `lang`, `wuid`, `wpid`
 * from the URL once at boot and exposes typed getters for the rest of the app.
 *
 * `widgetMode = true` makes the booking funnel render its "light" (chrome-less)
 * variant suitable for being embedded in an iframe by the train-results widget.
 */

export type WidgetCheckoutMode = 'cart' | 'payment'

export interface WidgetModeState {
    enabled: boolean
    checkoutMode: WidgetCheckoutMode
    uuid: string
    partnerId: string
}

export const defaultWidgetModeState = (): WidgetModeState => ({
    enabled: false,
    checkoutMode: 'cart',
    uuid: '',
    partnerId: '',
})

export function readWidgetModeFromUrl(): WidgetModeState {
    try {
        const params = new URLSearchParams(window.location.search)
        const enabled = params.get('widgetMode') === '1'
        if (!enabled) return defaultWidgetModeState()
        const wm = params.get('wm')
        const checkoutMode: WidgetCheckoutMode = wm === 'payment' ? 'payment' : 'cart'
        return {
            enabled: true,
            checkoutMode,
            uuid: params.get('wuid') || '',
            partnerId: params.get('wpid') || '',
        }
    } catch {
        return defaultWidgetModeState()
    }
}

/** Vuex module: kept namespaced=false to match the rest of BookingStore. */
export const widgetModeModule = {
    state: (): { widgetMode: WidgetModeState } => ({
        widgetMode: defaultWidgetModeState(),
    }),
    mutations: {
        setWidgetMode(state: { widgetMode: WidgetModeState }, payload: WidgetModeState) {
            state.widgetMode = { ...payload }
        },
    },
    getters: {
        getIsWidgetMode: (state: { widgetMode: WidgetModeState }) => state.widgetMode.enabled,
        getWidgetCheckoutMode: (state: { widgetMode: WidgetModeState }) => state.widgetMode.checkoutMode,
        getWidgetMeta: (state: { widgetMode: WidgetModeState }) => state.widgetMode,
    },
}
